import { isDefined } from 'Utils/type.utils';
import {
  CwpDatesDto,
  CwpDocumentDto,
  CwpDocumentsDto,
  DetailsCwpDto,
} from '../types/insurance.dto.types.cwp';
import {
  ComparedConditionCodes,
  ComparedCoverages,
  ComparedDocuments,
  ComparedPrices,
  ComparedStatus,
  CwpDates,
  CwpDocument,
  CwpDocuments,
  DetailsCwp,
} from '../types/insurance.types.cwp';

export const mapCwpDetails = (detailsCwp: DetailsCwpDto): DetailsCwp => {
  return {
    agreementCode: detailsCwp.agreementCode,
    conditionCode: mapConditionCodes(detailsCwp),
    coverages: mapCoverages(detailsCwp),
    dates: mapCwpDetailsDates(detailsCwp.dates),
    documents: mapDocuments(detailsCwp),
    hasFutureChanges: detailsCwp.hasFutureChanges,
    isCancelable: detailsCwp.isCancelable,
    isInRenewal: detailsCwp.isInRenewal,
    policyNumber: detailsCwp.policyNumber,
    price: mapPrices(detailsCwp),
    status: mapStatus(detailsCwp),
  };
};

const mapCwpDetailsDates = (dates: CwpDatesDto): CwpDates => {
  return {
    cancellationDate: isDefined(dates.cancellationDate)
      ? dates.cancellationDate
      : undefined,
    renewalDateCwp: dates.renewalDate,
    upcomingChangeDate: isDefined(dates.upcomingChangeDate)
      ? dates.upcomingChangeDate
      : undefined,
    originalIssueDate: dates.originalIssueDate,
    lastChangeDate: isDefined(dates.lastChangeDate)
      ? dates.lastChangeDate
      : undefined,
  };
};

const mapCoverages = (details: DetailsCwpDto): ComparedCoverages => {
  const {
    detailsOnTodaysDate,
    detailsAfterUpcomingChange,
    detailsAfterLastChange,
  } = details;
  return {
    today: detailsOnTodaysDate.coverages,
    afterUpcomingChange: isDefined(detailsAfterUpcomingChange)
      ? detailsAfterUpcomingChange.coverages
      : undefined,
    afterLastChange: isDefined(detailsAfterLastChange)
      ? detailsAfterLastChange.coverages
      : undefined,
  };
};

const mapConditionCodes = (details: DetailsCwpDto): ComparedConditionCodes => {
  const {
    detailsOnTodaysDate,
    detailsAfterUpcomingChange,
    detailsAfterLastChange,
  } = details;
  return {
    today: detailsOnTodaysDate.conditionCode,
    afterUpcomingChange: isDefined(detailsAfterUpcomingChange)
      ? detailsAfterUpcomingChange.conditionCode
      : undefined,
    afterLastChange: isDefined(detailsAfterLastChange)
      ? detailsAfterLastChange.conditionCode
      : undefined,
  };
};

const mapPrices = (details: DetailsCwpDto): ComparedPrices => {
  const {
    detailsOnTodaysDate,
    detailsAfterUpcomingChange,
    detailsAfterLastChange,
  } = details;
  return {
    today: detailsOnTodaysDate.price,
    afterUpcomingChange: isDefined(detailsAfterUpcomingChange)
      ? detailsAfterUpcomingChange.price
      : undefined,
    afterLastChange: isDefined(detailsAfterLastChange)
      ? detailsAfterLastChange.price
      : undefined,
  };
};

const mapDocuments = (details: DetailsCwpDto): ComparedDocuments => {
  const {
    detailsOnTodaysDate,
    detailsAfterUpcomingChange,
    detailsAfterLastChange,
  } = details;
  return {
    today: mapCwpDocuments(detailsOnTodaysDate.documents),
    afterUpcomingChange: isDefined(detailsAfterUpcomingChange)
      ? mapCwpDocuments(detailsAfterUpcomingChange.documents)
      : undefined,
    afterLastChange: isDefined(detailsAfterLastChange)
      ? mapCwpDocuments(detailsAfterLastChange.documents)
      : undefined,
  };
};

const mapStatus = (details: DetailsCwpDto): ComparedStatus => {
  const {
    detailsOnTodaysDate,
    detailsAfterUpcomingChange,
    detailsAfterLastChange,
  } = details;
  return {
    today: detailsOnTodaysDate.status,
    afterUpcomingChange: isDefined(detailsAfterUpcomingChange)
      ? detailsAfterUpcomingChange.status
      : undefined,
    afterLastChange: isDefined(detailsAfterLastChange)
      ? detailsAfterLastChange.status
      : undefined,
  };
};

const mapCwpDocuments = (documents: CwpDocumentsDto): CwpDocuments => {
  return {
    policyDocument: mapCwpDocument(documents.policyDocument),
    renewalDocument: mapCwpDocument(documents.renewalDocument),
    allPolicyDocuments: documents.allPolicyDocuments
      .map((document) => mapCwpDocument(document))
      .filter((document): document is CwpDocument => document !== undefined),
  };
};

const mapCwpDocument = (
  document: CwpDocumentDto | null,
): CwpDocument | undefined => {
  if (!isDefined(document)) {
    return undefined;
  }
  return {
    documentHref: document.documentHref,
    documentId: document.documentId,
    heading: document.heading,
  };
};
